import { KeyFunctionMap, RxCollection, RxCollectionCreator, RxDocument, RxJsonSchema } from "rxdb"
import { getDB } from "src/db/databases"
import {
  AttributeChar,
  AttributeCharValueBase,
  AttributeNumeric,
  AttributeNumericValueBase,
  AttributeSelect,
  AttributeSelectOptions,
  AttributeSelectValueBase,
  AttributeSelectWithOptions,
  AttributeTemporal,
  AttributeTemporalValueBase,
  AttributeValueCharFull,
  AttributeValueNumericFull,
  AttributeValueSelectFull,
  AttributeValueTemporalFull,
} from "src/util/types/dataModels/attribute"

//region Types

type AttributeRelations = "item" | "item_model" | "area" | "location"

export interface AttributeSelectDocumentMethods extends KeyFunctionMap {
  populateWithOptions: () => Promise<AttributeSelectWithOptions>
}

export interface AttributeCharValueCollectionMethods extends KeyFunctionMap {
  getValuesForEntity(
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeCharValueBase>[]>
}
export interface AttributeNumericValueCollectionMethods extends KeyFunctionMap {
  getValuesForEntity(
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeNumericValueBase>[]>
}
export interface AttributeSelectValueCollectionMethods extends KeyFunctionMap {
  getValuesForEntity(
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeSelectValueBase>[]>
}
export interface AttributeTemporalValueCollectionMethods extends KeyFunctionMap {
  getValuesForEntity(
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeTemporalValueBase>[]>
}

export type AttributeCharCollection = RxCollection<AttributeChar>
export type AttributeCharValueCollection = RxCollection<
  AttributeValueCharFull,
  object,
  AttributeCharValueCollectionMethods
>
export type AttributeNumericCollection = RxCollection<AttributeNumeric>
export type AttributeNumericValueCollection = RxCollection<
  AttributeValueNumericFull,
  object,
  AttributeNumericValueCollectionMethods
>
export type AttributeSelectCollection = RxCollection<
  AttributeSelect,
  AttributeSelectDocumentMethods
>
export type AttributeSelectOptionsCollection = RxCollection<AttributeSelectOptions>
export type AttributeSelectValueCollection = RxCollection<
  AttributeValueSelectFull,
  object,
  AttributeSelectValueCollectionMethods
>
export type AttributeTemporalCollection = RxCollection<AttributeTemporal>
export type AttributeTemporalValueCollection = RxCollection<
  AttributeValueTemporalFull,
  object,
  AttributeTemporalValueCollectionMethods
>

//endregion

/**
 * Schema for char type attribute collections
 */
export const attributeCharSchema: RxJsonSchema<AttributeChar> = {
  title: "Char attribute schema",
  description: "Char attributes for a particular user",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    name: { type: "string", maxLength: 100 },
    description: { type: "string" },
    order: { type: "number", minimum: 0, maximum: 32767 },
    autocomplete: { type: "boolean" },
    allow_blank: { type: "boolean" },
    max_length: { type: "number", minimum: 0, maximum: 32767 },
  },
  required: ["id", "autocomplete", "allow_blank"],
  indexes: ["updated_dt"],
}

export const attributeCharValueSchema: RxJsonSchema<AttributeValueCharFull> = {
  title: "Char attribute values schema",
  description: "Char attributes values for all attributes",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    value: { type: "string" },
    item: { ref: "item", type: "string", maxLength: 36 },
    item_model: { ref: "item_model", type: "string", maxLength: 36 },
    area: { ref: "area", type: "string", maxLength: 36 },
    location: { ref: "location", type: "string", maxLength: 36 },
    attribute: { ref: "attribute_char", type: "string", maxLength: 36 },
  },
  required: ["id", "attribute", "value"],
  indexes: ["attribute"],
}

/**
 * Schema for numeric type attribute collections
 */
export const attributeNumericSchema: RxJsonSchema<AttributeNumeric> = {
  title: "Numeric attribute schema",
  description: "Numeric attributes for a particular user",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    name: { type: "string", maxLength: 100 },
    description: { type: "string" },
    order: { type: "number", minimum: 0, maximum: 32767 },
    decimals: { type: "number", minimum: 0, maximum: 32767 },
    min: { type: "number" },
    max: { type: "number" },
  },
  required: ["id", "decimals"],
}

export const attributeNumericValueSchema: RxJsonSchema<AttributeValueNumericFull> = {
  title: "Numeric attribute values schema",
  description: "Numeric attributes values for all attributes",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    value: { type: "number" },
    item: { ref: "item", type: "string", maxLength: 36 },
    item_model: { ref: "item_model", type: "string", maxLength: 36 },
    area: { ref: "area", type: "string", maxLength: 36 },
    location: { ref: "location", type: "string", maxLength: 36 },
    attribute: { ref: "attribute_numeric", type: "string", maxLength: 36 },
  },
  required: ["id", "attribute", "value"],
  indexes: ["attribute"],
}

/**
 * Schema for select type attribute collections
 */
export const attributeSelectSchema: RxJsonSchema<AttributeSelect> = {
  title: "Select attribute schema",
  description: "Select attributes for a particular user",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    name: { type: "string", maxLength: 100 },
    description: { type: "string" },
    order: { type: "number", minimum: 0, maximum: 32767 },
    allow_multiple_selections: { type: "boolean" },
  },
  required: ["id", "allow_multiple_selections"],
}

/**
 * Schema for select options type attribute collections
 */
export const attributeSelectOptionsSchema: RxJsonSchema<AttributeSelectOptions> = {
  title: "Select options attribute schema",
  description: "Select options attributes for a particular user",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 32 },
    updated_dt: { type: "string", maxLength: 32 },
    value: { type: "string", maxLength: 200 },
    attribute: { ref: "attribute_select", type: "string", maxLength: 36 },
  },
  required: ["id", "attribute", "value"],
}

export const attributeSelectValueSchema: RxJsonSchema<AttributeValueSelectFull> = {
  title: "Char attribute values schema",
  description: "Char attributes values for all attributes",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    attribute_select_options: {
      type: "array",
      uniqueItems: true,
      items: { ref: "attribute_select_options", type: "string" },
    },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    item: { ref: "item", type: "string", maxLength: 36 },
    item_model: { ref: "item_model", type: "string", maxLength: 36 },
    area: { ref: "area", type: "string", maxLength: 36 },
    location: { ref: "location", type: "string", maxLength: 36 },
    attribute: { ref: "attribute_select", type: "string", maxLength: 36 },
  },
  required: ["id", "attribute", "attribute_select_options"],
  indexes: ["attribute"],
}

/**
 * Schema for temporal type attribute collections
 */
export const attributeTemporalSchema: RxJsonSchema<AttributeTemporal> = {
  title: "Temporal attribute schema",
  description: "Temporal attributes for a particular user",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    name: { type: "string", maxLength: 100 },
    description: { type: "string" },
    order: { type: "number", minimum: 0, maximum: 32767 },
    enable_date: { type: "boolean" },
    enable_time: { type: "boolean" },
  },
  required: ["id", "enable_date", "enable_time"],
}

export const attributeTemporalValueSchema: RxJsonSchema<AttributeValueTemporalFull> = {
  title: "Temporal attribute values schema",
  description: "Temporal attributes values for all attributes",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    id: { type: "string", maxLength: 36, uniqueItems: true },
    created_dt: { type: "string", maxLength: 64 },
    updated_dt: { type: "string", maxLength: 64 },
    date: { type: "string" },
    time: { type: "string" },
    item: { ref: "item", type: "string", maxLength: 36 },
    item_model: { ref: "item_model", type: "string", maxLength: 36 },
    area: { ref: "area", type: "string", maxLength: 36 },
    location: { ref: "location", type: "string", maxLength: 36 },
    attribute: { ref: "attribute_temporal", type: "string", maxLength: 36 },
  },
  required: ["id", "attribute"],
  indexes: ["attribute"],
}

export const attributeCharValueCollectionMethods: AttributeCharValueCollectionMethods = {
  async getValuesForEntity(
    this: AttributeCharValueCollection,
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeCharValueBase>[]> {
    return this.find().where(entity).eq(entityID).exec()
  },
}

export const attributeNumericValueCollectionMethods: AttributeNumericValueCollectionMethods = {
  async getValuesForEntity(
    this: AttributeNumericValueCollection,
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeNumericValueBase>[]> {
    return this.find().where(entity).eq(entityID).exec()
  },
}

export const attributeSelectValueCollectionMethods: AttributeSelectValueCollectionMethods = {
  async getValuesForEntity(
    this: AttributeSelectValueCollection,
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeSelectValueBase>[]> {
    return this.find().where(entity).eq(entityID).exec()
  },
}

export const attributeTemporalValueCollectionMethods: AttributeTemporalValueCollectionMethods = {
  async getValuesForEntity(
    this: AttributeTemporalValueCollection,
    entity: AttributeRelations,
    entityID?: string
  ): Promise<RxDocument<AttributeTemporalValueBase>[]> {
    return this.find().where(entity).eq(entityID).exec()
  },
}

// TODO: Is this even needed any more?
export const attributeSelectMethods: AttributeSelectDocumentMethods = {
  async populateWithOptions(this: RxDocument<AttributeSelect>) {
    const db = await getDB()
    const options = await db.attribute_select_options
      .find()
      .where("attribute")
      .equals(this.id)
      .exec()

    const mutatedSelectAttribute = this.toMutableJSON()
    const mutatedOptions = options.map((option) => option.toMutableJSON())

    const populatedSelectAttribute: AttributeSelectWithOptions = {
      ...mutatedSelectAttribute,
      options: mutatedOptions,
    }

    return populatedSelectAttribute
  },
}

// A set of collection creators that can be passed to the database when setting up collections

export const attributeCharCollectionCreator: RxCollectionCreator = {
  schema: attributeCharSchema,
}

export const attributeCharValueCollectionCreator: RxCollectionCreator = {
  schema: attributeCharValueSchema,
  statics: attributeCharValueCollectionMethods,
}

export const attributeNumericCollectionCreator: RxCollectionCreator = {
  schema: attributeNumericSchema,
}

export const attributeNumericValueCollectionCreator: RxCollectionCreator = {
  schema: attributeNumericValueSchema,
  statics: attributeNumericValueCollectionMethods,
}

export const attributeSelectCollectionCreator: RxCollectionCreator = {
  schema: attributeSelectSchema,
  methods: attributeSelectMethods,
}

export const attributeSelectOptionsCollectionCreator: RxCollectionCreator = {
  schema: attributeSelectOptionsSchema,
}

export const attributeSelectValueCollectionCreator: RxCollectionCreator = {
  schema: attributeSelectValueSchema,
  statics: attributeSelectValueCollectionMethods,
}

export const attributeTemporalCollectionCreator: RxCollectionCreator = {
  schema: attributeTemporalSchema,
}

export const attributeTemporalValueCollectionCreator: RxCollectionCreator = {
  schema: attributeTemporalValueSchema,
  statics: attributeTemporalValueCollectionMethods,
}
